import { Directive, EventEmitter, Injectable, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';

import {
  FlexCheckInStatuses,
  FlexTimeActivityInstance,
  FlexTimePeriod,
  HallPassStatusEnum,
  IHallPassType,
  IMingaFeatureToggleKeys,
} from 'libs/domain';
import { PbisCategory } from 'libs/shared';
import { mingaSettingTypes } from 'libs/util';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ViewIdRoutes } from '@app/src/app/shared/components/view-id';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { BehaviorMsgCategory } from '@modules/behavior-manager/components/bm-types/constants';
import { HpmDashboardTableItem } from '@modules/hallpass-manager';
import { PeopleViewProfileRoute } from '@modules/people/components/people-view-profile/constants/people-view-profile.constants';
import { PeopleRoute } from '@modules/people/types';

import { ALIAS_BREAKPOINT_MAP, MediaService } from '@shared/services/media';

import { MyClassMessages } from '../constants/tt-my-class.constants';
import {
  AssignmentType,
  FlexAssignmentType,
  MyClassSortColumns,
  PastAssignment,
  Student,
} from '../types/tt-my-class.types';
import { groupAndCount } from '../utils/my-class.utils';
import { MyClassFlexTimeService } from './my-class-flex-time.service';
import { MyClassHallPassService } from './my-class-hallpasses.service';

type ActionItemWithCount = PastAssignment & { count: number };

@Directive()
@Injectable()
export class StudentItemBaseDirective implements OnDestroy {
  private _destroyedSubject = new ReplaySubject<void>(1);
  private _pastAssignmentsSubject = new BehaviorSubject<PastAssignment[]>([]);
  private _pastAssignments$ = this._pastAssignmentsSubject.asObservable();
  private _student: Student;
  private _studentSubject = new BehaviorSubject<Student>(null);

  @Input() canViewProfile: boolean;
  @Input() selected: boolean;
  @Input() authHash: string;
  @Input() hidePicture: boolean;
  @Input() firstNameFirst: boolean;
  @Input() set student(value: Student) {
    this._student = value;
    this._studentSubject.next(value);
  }
  get student(): Student {
    return this._student;
  }
  @Input() showPulse: boolean;
  @Input() set pastAssignments(value: PastAssignment[]) {
    this._pastAssignmentsSubject.next(value);
  }
  @Input() hallPass: HpmDashboardTableItem & { type: IHallPassType };
  @Input() upcomingFlexActivity?: FlexTimeActivityInstance;
  @Input() upcomingPeriod?: FlexTimePeriod;
  @Input() addSortHeaders: boolean;
  @Input() showCheckIn: boolean;
  @Input() showFlexTime: boolean;
  @Input() showBehaviors: boolean;
  @Input() praisesAssigned: boolean;
  @Input() guidancesAssigned: boolean;
  @Output() toggleSelected = new EventEmitter<boolean>();
  @Output() hallPassChange = new EventEmitter<{
    action: 'end' | 'deny' | 'approve';
    hallPass: HpmDashboardTableItem & { type: IHallPassType };
    opts?: {
      skipConfirmation?: boolean;
    };
  }>();
  @Output() flexRegistration = new EventEmitter<{ action: number; students: Student[] }>();
  @Output() sortUpdated = new EventEmitter<MyClassSortColumns>();

  public pastPraises$: Observable<{
    totalCount: number;
    items: ActionItemWithCount[];
  }> = this._pastAssignments$.pipe(
    map(assignments => {
      const praises = assignments.filter(a => {
        const data = a.data as any;
        return a.assignmentType === AssignmentType.BEHAVIOR && data.categoryId === PbisCategory.PRAISE;
      });

      return {
        items: groupAndCount(praises),
        totalCount: praises.length,
      };
    }),
  );

  public pastPraiseConsequences$: Observable<{
    totalCount: number;
    items: ActionItemWithCount[];
  }> = this._pastAssignments$.pipe(
    map(assignments => {
      const consequences = assignments.filter(a => {
        const data = a.data as any;
        return a.assignmentType === AssignmentType.CONSEQUENCE && data.categoryId === BehaviorMsgCategory.PRAISE;
      });

      return {
        items: groupAndCount(consequences),
        totalCount: consequences.length,
      };
    }),
  );

  public pastGuidances$: Observable<{
    totalCount: number;
    items: ActionItemWithCount[];
  }> = this._pastAssignments$.pipe(
    map(assignments => {
      const guidances = assignments.filter(a => {
        const data = a.data as any;
        return a.assignmentType === AssignmentType.BEHAVIOR && data.categoryId === PbisCategory.GUIDANCE;
      });

      return {
        items: groupAndCount(guidances),
        totalCount: guidances.length,
      };
    }),
  );

  public pastConsequences$: Observable<{
    totalCount: number;
    items: ActionItemWithCount[];
  }> = this._pastAssignments$.pipe(
    map(assignments => {
      const consequences = assignments.filter(a => {
        const data = a.data as any;
        return a.assignmentType === AssignmentType.CONSEQUENCE && data.categoryId === BehaviorMsgCategory.GUIDANCE;
      });

      return {
        items: groupAndCount(consequences),
        totalCount: consequences.length,
      };
    }),
  );

  public pastCheckIns$: Observable<
    Array<{
      title: string;
      name: string;
      checkedInAt: Date;
    }>
  > = combineLatest([this._pastAssignments$, this._studentSubject.asObservable()]).pipe(
    map(([assignments, student]) => {
      const checkInReasons = assignments
        .filter(a => {
          return a.assignmentType === AssignmentType.CHECK_IN;
        })
        .map(a => {
          const data = a.data as any;
          return {
            title: 'Check in reason',
            name: data.name,
            checkedInAt: a.assignedAt,
          };
        });

      const flexCheckIns = [];

      if ([FlexCheckInStatuses.IN, FlexCheckInStatuses.TARDY].includes(student.flexTimeActivity?.status as any)) {
        flexCheckIns.push({
          title:
            student.flexTimeActivity?.status === FlexCheckInStatuses.TARDY
              ? 'Flextime tardy check in'
              : 'Flextime check in',
          name: student.flexTimeActivity.instance.flexTimeActivity.name,
          checkedInAt: student.flexTimeActivity?.checkedInAt,
        });
      }

      return [...flexCheckIns, ...checkInReasons];
    }),
  );

  public PASS_STATUS_TO_TYPE = {
    [HallPassStatusEnum.ACTIVE]: 'default',
    [HallPassStatusEnum.PENDING_APPROVAL]: 'warning',
    [HallPassStatusEnum.OVERDUE]: 'error',
    [HallPassStatusEnum.ENDED]: 'muted',
  };

  public PASS_STATUS_TO_CARD_TYPE = {
    [HallPassStatusEnum.PENDING_APPROVAL]: 'pending',
    [HallPassStatusEnum.OVERDUE]: 'overdue',
  };

  public HallPassStatus = HallPassStatusEnum;

  public FlexCheckInStatuses = FlexCheckInStatuses;

  public MESSAGES = MyClassMessages;

  public readonly recentlyEndedSetting$ = this._mingaSettings.getSettingValueObs(
    mingaSettingTypes.PASS_RECENTLY_ENDED_FILTER,
  );

  public manuallyEndedPassesSetting: boolean;
  public readonly manuallyEndedPassesSetting$ = this._mingaSettings
    .getSettingValueObs(mingaSettingTypes.PASS_MUST_MANUALLY_END)
    .pipe(
      tap(value => {
        this.manuallyEndedPassesSetting = value;
      }),
    );

  /**
   * We need at least one of these features enabled to show the check in icon.
   */
  public isCheckInEnabled$ = combineLatest([
    this._mingaSettings.getModuleEnabled(IMingaFeatureToggleKeys.FLEX_TIME_ENABLED),
    this._mingaSettings.getModuleEnabled(IMingaFeatureToggleKeys.CHECKIN_ENABLED),
  ]).pipe(
    map(([flexEnabled, checkInEnabled]) => {
      return flexEnabled || checkInEnabled;
    }),
  );

  constructor(
    public mediaService: MediaService,
    private _router: Router,
    private _mingaSettings: MingaSettingsService,
    public myClassHallPassService: MyClassHallPassService,
    public myClassFlexTimeService: MyClassFlexTimeService,
  ) {}

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  public get classes() {
    const classes = {
      selected: this.selected,
      'no-avatar': this.hidePicture,
      'first-name-first': this.firstNameFirst,
      'show-pulse': this.showPulse,
    };

    if (this.hallPass && this.PASS_STATUS_TO_CARD_TYPE[this.hallPass.status.state]) {
      const status = this.PASS_STATUS_TO_CARD_TYPE[this.hallPass.status.state];
      classes[status] = true;
    }

    return classes;
  }

  public toggle() {
    this.toggleSelected.emit(!this.selected);
  }

  public actionsBarClicked(event, breakpoint) {
    // on mobile we don't want the action bar to toggle the selected state
    if (breakpoint === ALIAS_BREAKPOINT_MAP.xs || breakpoint === ALIAS_BREAKPOINT_MAP.sm) {
      event.stopPropagation();
    }
  }

  public updateSort(event: MyClassSortColumns) {
    this.sortUpdated.emit(event);
  }

  public getStudentIdRoute(hash) {
    return ['', { outlets: { o: [ViewIdRoutes.ROOT, hash] } }];
  }

  public getProfileRoute(hash) {
    return `/${PeopleRoute.ROOT}/${PeopleViewProfileRoute.PROFILE}/${hash}/${PeopleViewProfileRoute.DASHBOARD}`;
  }

  public matMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      return;
    }
    event.stopPropagation();
  }

  public showIcon(): boolean {
    return !(this.hallPass.type?.manuallyEndPass ?? this.manuallyEndedPassesSetting);
  }

  public async onHallPassActionClicked(
    action: 'end' | 'deny' | 'approve',
    opts?: {
      skipConfirmation?: boolean;
    },
  ) {
    this.hallPassChange.emit({ action, hallPass: this.hallPass, opts });
  }

  public async flexActivityAction(action: 'register' | 'assign') {
    this.flexRegistration.emit({
      action: action === 'register' ? FlexAssignmentType.REGISTER : FlexAssignmentType.ASSIGN,
      students: [this.student],
    });
  }
}
